import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { logError } from 'utils/logger';
import { UPDATE_PROPOSAL, GET_PROPOSAL_SNAPSHOT } from '../api';
import { ProposalSnapshotResponse, ProposalSnapshotVariables } from '../api/types';

export function useProposal() {
  const { showNotification } = useNotifications();

  const [getSnapshot] = useLazyQuery<ProposalSnapshotResponse, ProposalSnapshotVariables>(GET_PROPOSAL_SNAPSHOT);

  const [updateProposal, { loading: isProposalUpdating }] = useMutation(UPDATE_PROPOSAL, {
    context: { authRequired: true },
    refetchQueries: ['Proposal'],
  });

  const handleUpdateProposal = useCallback(
    async (id: string, fieldsObj: Record<string, any>) => {
      try {
        await updateProposal({ variables: { id, fieldsObj } });

        showNotification('Status updated!');
      } catch (error) {
        logError(error);
        showNotification('Status not updated!', { type: 'error' });
      }
    },
    [showNotification, updateProposal]
  );

  const handleGetProposalSnapshot = useCallback(
    async (proposalId: string) => {
      const { data } = await getSnapshot({ variables: { id: proposalId } });
      return data?.proposal?.snapshot ?? null;
    },
    [getSnapshot]
  );

  return {
    isProposalUpdating,
    handleUpdateProposal,
    handleGetProposalSnapshot,
  };
}
